if (!customElements.get('sticky-atc-bar-minimalist')) {
  customElements.define(
    'sticky-atc-bar-minimalist',
    class StickyAtcBarMinimalist extends HTMLElement {
      constructor() {
        super();

        this.classes = {
          isOpen: 'sticky-atc-bar--show',
        };

        this.submitEl.addEventListener('click', this.onClick.bind(this));
      }

      get submitEl() {
        return (this._submitEl = this._submitEl || this.querySelector('[type="submit"]'));
      }

      connectedCallback() {
        this.productFormActions = document.getElementById(`product-form-${this.dataset.section}`) || document.querySelector('.main-product-form');
        this.mainProductInfo = document.querySelector(`product-info[data-section="${this.dataset.section}"]`) || document.querySelector('product-info');

        this.init();

        this.cartErrorUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartError, () => {
          this.resetButtonLoading();
          this.scrollToTop(this.productFormActions);
        });

        this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, () => {
          this.resetButtonLoading();
        });
      }

      disconnectedCallback() {
        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber();
        }

        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }

        if (this._handleStickyVisibilityScroll) {
          window.removeEventListener('scroll', this._handleStickyVisibilityScroll);
        }
        if (this.observer) this.observer.disconnect();
        if (this.footerObserver) this.footerObserver.disconnect();
      }

      init() {
        if (!this.productFormActions) {
          this.classList.remove(this.classes.isOpen);
          return;
        }

        const observedElement =
          this.productFormActions.querySelector('button[name="add"], button[type="submit"], .product-form__submit') ||
          this.productFormActions;
        let isMainAtcVisible = true;
        let isFooterVisible = false;

        const hasScrolledPastMainAtc = () => {
          const rect = observedElement.getBoundingClientRect();
          return window.scrollY > 8 && rect.bottom <= 0;
        };

        const updateVisibility = () => {
          const shouldShow = !isMainAtcVisible && hasScrolledPastMainAtc() && !isFooterVisible;
          this.classList.toggle(this.classes.isOpen, shouldShow);
        };

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              isMainAtcVisible = entry.isIntersecting;
              updateVisibility();
            });
          },
          { threshold: 0 }
        );
        this.observer.observe(observedElement);

        const footerEl =
          document.querySelector('.footer') ||
          document.querySelector('footer') ||
          document.querySelector('[id*="footer"]');
        if (footerEl) {
          this.footerObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                isFooterVisible = entry.isIntersecting;
                updateVisibility();
              });
            },
            { threshold: 0 }
          );
          this.footerObserver.observe(footerEl);
        }

        this._handleStickyVisibilityScroll = updateVisibility;
        window.addEventListener('scroll', this._handleStickyVisibilityScroll, { passive: true });
        updateVisibility();
        this.syncWithMainProductForm();
      }

      onClick() {
        this.submitEl.setAttribute('aria-disabled', 'true');
        this.submitEl.classList.add('btn--loading');
      }

      resetButtonLoading() {
        this.submitEl.removeAttribute('aria-disabled');
        this.submitEl.classList.remove('btn--loading');
      }

      scrollToTop(target, offset = 200) {
        const scrollIntoView = (selector, offset) => {
          window.scrollTo({
            behavior: 'smooth',
            top: selector.getBoundingClientRect().top - document.body.getBoundingClientRect().top - offset,
          });
        };

        scrollIntoView(target, offset);
      }

      updateButton(disable = true, text, modifyClass = true) {
        const addButton = this.querySelector('[name="add"]');
        if (!addButton) return;

        const addButtonText = addButton.querySelector('.btn__text') || addButton.querySelector('span');
        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.textContent = FoxTheme.variantStrings.addToCart;
        }
      }

      updatePrice(variant) {
        if (!variant) return;
        const classes = {
          onSale: 'f-price--on-sale',
          soldOut: 'f-price--sold-out',
        };
        const selectors = {
          priceWrapper: '.f-price',
          salePrice: '.f-price-item--sale',
          compareAtPrice: ['.f-price-item--regular'],
          unitPriceWrapper: '.f-price__unit-wrapper',
        };
        const moneyFormat = FoxTheme.settings.moneyFormat;
        const { priceWrapper, salePrice, unitPriceWrapper, compareAtPrice } = FoxTheme.utils.queryDomNodes(
          selectors,
          this
        );
        const unitPrice = unitPriceWrapper.querySelector('.f-price__unit');

        const { compare_at_price, price, unit_price_measurement } = variant;

        // On sale.
        if (compare_at_price && compare_at_price > price) {
          priceWrapper.classList.add(classes.onSale);
        } else {
          priceWrapper.classList.remove(classes.onSale);
        }

        // Sold out.
        if (!variant.available) {
          priceWrapper.classList.add(classes.soldOut);
        } else {
          priceWrapper.classList.remove(classes.soldOut);
        }

        if (salePrice) salePrice.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat);

        if (compareAtPrice && compareAtPrice.length && compare_at_price > price) {
          compareAtPrice.forEach(
            (item) => (item.innerHTML = `<s>${FoxTheme.Currency.formatMoney(compare_at_price, moneyFormat)}</s>`)
          );
        } else {
          compareAtPrice.forEach((item) => (item.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat)));
        }

        if (unit_price_measurement && unitPrice) {
          unitPriceWrapper.classList.remove('hidden');
          const unitPriceContent = `<span>${FoxTheme.Currency.formatMoney(
            variant.unit_price,
            moneyFormat
          )}</span>/<span data-unit-price-base-unit>${this._getBaseUnit()}</span>`;
          unitPrice.innerHTML = unitPriceContent;
        } else {
          unitPriceWrapper.classList.add('hidden');
        }
      }

      updateImage(variant) {
        if (!variant || !variant.featured_image) return;
        
        const src = variant.featured_image.src || variant.featured_image.url;
        if (!src) return;

        const images = this.querySelectorAll('.sticky-atc-bar__product-image img');
        images.forEach((img) => {
          img.setAttribute('src', src);
          img.setAttribute('srcset', src);
        });
      }

      syncWithMainProductForm() {
        // PRIMARY: Watch the main product form's hidden variant input.
        const mainForm = this.productFormActions;
        if (mainForm) {
          const mainVariantInput = mainForm.querySelector('input[name="id"]');
          if (mainVariantInput) {
            mainVariantInput.addEventListener('change', () => {
              // Read the variant directly from product-info's live state
              const variant = this.mainProductInfo && this.mainProductInfo.currentVariant;
              if (variant) {
                this._applyVariantToStickyBar(variant);
              }
            });
          }
        }

        // FALLBACK: FoxTheme PubSub
        if (window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.subscribe && FoxTheme.pubsub.PUB_SUB_EVENTS) {
          FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
            if (!event || !event.data || !event.data.variant) return;
            this._applyVariantToStickyBar(event.data.variant);
          });
        }

        // FALLBACK: document-level variant:changed custom event
        document.addEventListener('variant:changed', (event) => {
          if (!event.detail || !event.detail.variant) return;
          this._applyVariantToStickyBar(event.detail.variant);
        });

        // INITIAL SYNC: Apply the variant from the URL on page load.
        requestAnimationFrame(() => {
          const initialVariant = this.mainProductInfo && this.mainProductInfo.currentVariant;
          if (initialVariant) {
            this._applyVariantToStickyBar(initialVariant);
          } else {
            const urlParams = new URLSearchParams(window.location.search);
            const urlVariantId = urlParams.get('variant');
            const formVariantId = mainForm && mainForm.querySelector('input[name="id"]')?.value;
            const variantId = urlVariantId || formVariantId;
            if (variantId && this.variantData) {
              const variant = this.variantData.find((v) => v.id === Number(variantId));
              if (variant) this._applyVariantToStickyBar(variant);
            }
          }
        });
      }

      _applyVariantToStickyBar(variant) {
        if (!variant) return;

        this.updatePrice(variant);
        this.updateImage(variant);
        this.updateButton(true, '', false);
        if (!variant.available) {
          this.updateButton(true, FoxTheme.variantStrings.soldOut, true);
        } else {
          this.updateButton(false, '', true);
        }
      }
    }
  );
}
